import RetailManager from 'app/retail_manager/RetailManager';
import Localize from 'app/src/localize/Localize';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import { createReactRenderer } from 'helpers/react-renderer';
import { trackAnalyticsEvent } from 'root/apps/helpers/AnalyticsTracking';
import Modules from 'shared_modules/Modules';

import { QuickPayCashModal } from './components/QuickPayCashModal.tsx';
import { QuickPayPaymentModal } from './components/QuickPayPaymentModal.tsx';
import TerminalPaymentModal from './components/TerminalPaymentModal';
// eslint-disable-next-line
let _quickPayCashModal = null;
// eslint-disable-next-line
let _quickPayCreditModal = null;
const REGISTER_COMPLETE_A_SALE_WITHOUT_SELECTING_A_SERIAL_NUMBER_STR =
  'register_complete_a_sale_without_selecting_a_serial_number';

// `doCashQuickPay` to be removed when the quick pay fixes are GA'd as it will remove the Cash option
export function doCashQuickPay(saleManager) {
  PaymentAnalytics.trackGenericPaymentEvent('QuickPay Cash', 'Start', saleManager.sale.id);

  // We make sure to refresh the sale right before rendering the quick-pay modal
  // This fix an issue where the merchant is editing the quantity
  // and clicks on the Card or Cash button without giving
  // enough time to the sale manager to update
  const RenderedComponent = createReactRenderer({
    component: QuickPayCashModal,
    selector: '[data-react-root="ReactModal"]',
    props: {
      onHide: () => {
        _quickPayCashModal = null;
        RenderedComponent.unmount();
      },
      onFinishSale: (cashAmount) =>
        new Promise((resolve, reject) => {
          saleManager
            .addPayment(PaymentFactory.createPayment(PaymentTypeManager.Types.CASH, cashAmount))
            .done(() => {
              window.merchantos.register
                .completeSale()
                .done(() => {
                  PaymentAnalytics.trackGenericPaymentEvent(
                    'QuickPay Cash',
                    'Completed',
                    saleManager.sale.id
                  );

                  resolve();
                })
                .fail(() => {
                  reject('The sale could not be completed.');
                });
            })
            .fail(() => {
              reject('Could not add the payment to the sale.');
            });
        }),
      // We can only have one cash payment in the sale so let's eliminate the current affect
      // of cash payments from the sale so that we can treat the lump sum a bit more succinctly.
      saleManager,
    },
  });
  _quickPayCashModal = RenderedComponent.mount();
}

export async function doCreditQuickPay({
  saleManager,
  paymentProvider,
  amount = null,
  tipAmount = null,
  terminal = null,
  terminalSelectionOption = false,
}) {
  const quickPayV2 = Modules.hasModule('gui.payment.quickpayV2');

  PaymentAnalytics.trackGenericPaymentEvent('QuickPay Credit', 'Accessed', saleManager.sale.id);
  let RenderedComponent;
  const terminalCollection = paymentProvider?.terminalCollection || [];
  const hasTerminal = terminalCollection.length > 0;
  const defaultQuickpayTerminalSelection =
    (await window.lsCloud.Options.get('default_quickpay_terminal_selection')) === '1';

  if (quickPayV2 && hasTerminal && !terminalSelectionOption && !defaultQuickpayTerminalSelection) {
    RenderedComponent = createReactRenderer({
      component: QuickPayPaymentModal,
      selector: '[data-react-root="ReactModal"]',
      props: {
        onHide: () => {
          _quickPayCreditModal = null;
          RenderedComponent.unmount();
        },
        onPaymentComplete: () =>
          new Promise((resolve, reject) => {
            if (saleManager.pendingBalance.isZero()) {
              window.merchantos.register
                .completeSale()
                .done(() => {
                  resolve();
                })
                .fail(() => {
                  reject('Sale not completed');
                });
            } else {
              // still have an outstanding balance for some reason so
              // take the user to the regular payments screen
              window.merchantos.register.donePay();
              resolve();
            }
          }),
        initialAmount: amount || saleManager.pendingBalance,
        saleManager,
        paymentProvider,
        ...(terminal && { terminal }),
        ...(tipAmount && { tipAmount }),
      },
    });
  } else {
    if (terminalSelectionOption) {
      trackAnalyticsEvent({
        eventCategory: 'QuickPay',
        eventName: 'Terminal Selection clicked',
        eventAction: 'Sale Continued',
        eventLabel: 'QuickPay-V2',
      });
    }
    RenderedComponent = createReactRenderer({
      component: TerminalPaymentModal,
      selector: '[data-react-root="ReactModal"]',
      props: {
        onHide: () => {
          _quickPayCreditModal = null;
          RenderedComponent.unmount();
        },
        onPaymentComplete: () =>
          new Promise((resolve, reject) => {
            if (saleManager.pendingBalance.isZero()) {
              window.merchantos.register
                .completeSale()
                .done(() => {
                  resolve();
                })
                .fail(() => {
                  reject('Sale not completed');
                });
            } else {
              // still have an outstanding balance for some reason so
              // take the user to the regular payments screen
              window.merchantos.register.donePay();
              resolve();
            }
          }),
        analyticsName: 'Quick Pay',
        initialAmount: amount || saleManager.pendingBalance,
        saleManager,
        paymentProvider,
        autoFinishSale: true,
        ...(terminal && { terminal }),
        ...(tipAmount && { tipAmount }),
      },
    });
  }

  _quickPayCreditModal = RenderedComponent.mount();
}

export async function setQuickPayState(saleManager, paymentProvider) {
  const hasFinancialServicesRights = !!window.merchantos.session.hasRight(
    'admin_financial_services'
  );

  const hasNoCustomerAttached = saleManager.sale.attributes.customerID <= 0;

  const requiresCustomerForSerializedSales =
    (await window.lsCloud.Options.get('require_customer_for_serialized_sales')) === '1';

  let saleLines = saleManager?.sale?.attributes?.SaleLines?.SaleLine || [];
  if (!Array.isArray(saleLines)) {
    saleLines = [saleLines];
  }

  const serialNumberPresent = saleLines.some((saleLine) => {
    // Need to call hasOwnProperty like this due to ES Lint rule:
    // Do not access Object.prototype method 'hasOwnProperty' from target object.
    return Object.prototype.hasOwnProperty.call(saleLine, 'Serialized');
  });

  // Module guard
  const quickPayFixV1 = Modules.hasModule('gui.payments.quick_pay_fixes_v1');

  // Does the user have rights to sell a serialized item without a serial number?
  const hasRightToSellSerializedItemsWithoutSerialNumber = window.merchantos.session.hasRight(
    REGISTER_COMPLETE_A_SALE_WITHOUT_SELECTING_A_SERIAL_NUMBER_STR
  );

  const serialItemsWithoutSerialNumberPresent = saleLines.some((saleLine) => {
    return (
      saleLine.Item &&
      saleLine.Item.serialized === 'true' &&
      !Object.prototype.hasOwnProperty.call(saleLine, 'Serialized')
    );
  });

  // LSR-24728 When the option `require_customer_for_serialized_sales` is enabled, the user must attach a customer to the sale
  // to continue if a serial number is associated with one or more line items.
  // Previous attempts to fix this were done in the backend via a call to `PUT /API/Account/{accountID}/Sale/{saleID}.json`, in which
  // a check was done on the transaction to see if a customer was attached to the sale. If not we would return an error message.
  //
  // This works well for quick pay via cash, but not quick pay via card since we don't execute this call until AFTER the calls to stripe
  // were made and a payment was taken. This resulted in The quick pay state disabling each quick pay payment option.
  //
  // Once the call was made to the aforementioned endpoint, the check would finally be executed, but would fail.
  // Because of this, the payment would become a partial payment. This may have caused some misaligned data between our system and stipe.
  //
  // The sales associate could click `Payment`, and remove the credit card balance present, and the state would be reset for the
  // quick pay payment options (but this is very hidden). Now, we do the check in the frontend before making any calls to stripe.
  // The backend check still exists in `includes/business/component/Sale.class.php`.

  if (
    requiresCustomerForSerializedSales &&
    serialNumberPresent &&
    hasNoCustomerAttached &&
    window.location.href.includes('register.php') // Checks were being executed in other pages such as the customer listing page.
  ) {
    $('#creditQuickPay').prop('disabled', true);
    $('#cashQuickPay').prop('disabled', true);
    window.alertUser(
      Localize.translate('Serial Number - Requires a customer be associated with the sale.'),
      300
    );

    // For testing purposes.
    return false;
  }

  // This is different from the previous as that was a check to see if the shop had the setting enabled to require a customer
  // for serialized sales.
  // If there were serialized items present with serials
  // and the user did not have a customer attached to the sale.
  //
  // For this one:
  // Module Guard, then even if the user HAS the right to sell serialized items without a serial number,
  // and we have serialized items without serial number present on the sale,
  // HOWEVER, if the shop setting still requires a customer to be associated with the sale for serialized items,
  // and the user does not have a customer attached to the sale, disable quick pay.
  // Once this module gets GA'd we can remove the module guard, and bring the two checks together, or parameterize them
  if (
    quickPayFixV1 &&
    hasRightToSellSerializedItemsWithoutSerialNumber &&
    requiresCustomerForSerializedSales &&
    serialItemsWithoutSerialNumberPresent &&
    hasNoCustomerAttached &&
    window.location.href.includes('register.php') // Checks were being executed in other pages such as the customer listing page.
  ) {
    $('#creditQuickPay').prop('disabled', true);
    $('#cashQuickPay').prop('disabled', true);
    window.alertUser(
      Localize.translate('Serial Number - Requires a customer be associated with the sale'),
      300
    );

    // For tests
    return false;
  }

  // Module Guard, and Additional check to see if a Serialized Items without a Serial Number is present
  // but also where the user is without the proper role rights to sell serialized items without a serial number.
  if (
    quickPayFixV1 &&
    serialItemsWithoutSerialNumberPresent &&
    !hasRightToSellSerializedItemsWithoutSerialNumber &&
    window.location.href.includes('register.php') // Checks were being executed in other pages such as the customer listing page.
  ) {
    $('#creditQuickPay').prop('disabled', true);
    $('#cashQuickPay').prop('disabled', true);
    window.alertUser(
      Localize.translate(
        'Serial Number - User does not have the right to sell serialized items without a serial number.'
      ),
      300
    );

    // For tests
    return false;
  }

  if (!RetailManager.active) {
    const { saleTotal, pendingPaymentsTotal, deposits, sale, customer } = saleManager;
    let hasDiscountsThatRequireCustomer = false;

    // Module guard, and if there's no customer attached, check if there are any discounts applied that requires a customer
    if (quickPayFixV1 && !customer) {
      hasDiscountsThatRequireCustomer = sale.attributes.SaleLines?.SaleLine.some((line) => {
        return line.Discount && line.Discount.requireCustomer === 'true';
      });
    }

    const quickPayDisabled =
      saleTotal.isZero() ||
      !pendingPaymentsTotal.isZero() ||
      !deposits.depositTotal.isZero() ||
      !deposits.refundTotal.isZero() ||
      hasDiscountsThatRequireCustomer;

    if (quickPayDisabled) {
      window.merchantos.keyboard.unsubscribe('Alt+.');
      window.merchantos.keyboard.unsubscribe('Alt+,');
    } else {
      window.merchantos.keyboard.subscribe('Alt+.', () => {
        doCashQuickPay(saleManager);
      });
      window.merchantos.keyboard.subscribe('Alt+,', () => {
        doCreditQuickPay(saleManager, paymentProvider);
      });
    }

    $('#creditQuickPay').prop('disabled', quickPayDisabled);
    $('#cashQuickPay').prop('disabled', quickPayDisabled);

    $('#quickPay').toggleClass('hidden', saleManager.isRefundSale());

    // For testing purposes.
    if (quickPayDisabled) {
      return false;
    }
  }

  if (
    quickPayFixV1 &&
    paymentProvider.activeGatewayRequiresCustomer() &&
    hasNoCustomerAttached &&
    window.location.href.includes('register.php') // Checks were being executed in other pages such as the customer listing page.
  ) {
    $('#creditQuickPay').prop('disabled', true);
    $('#cashQuickPay').prop('disabled', false);

    // For tests
    return false;
  }

  // For testing purposes.
  return true;
}
